// Faz BO (2026-05-10) — Topbar
// Faz BP-polish (2026-05-13): kullanici Avatar baslangic harfi (eski hep "A"
// gosteriyordu), logout confirm dialog (yanlislikla cikis koruma), avatar
// tiklaninca acilan kullanici menusu (yakinda profil/ayarlar eklenecek).
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar, Toolbar, Box, Typography, Avatar, IconButton,
  Menu, MenuItem, ListItemIcon, ListItemText, Divider,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import PersonIcon from "@mui/icons-material/Person";
import NotificationBell from "../NotificationBell";
import { useThemeMode } from "../../theme";
import { clearAuth } from "../../utils/authStorage";

// localStorage.user'dan kullanici adi/email cek — yoksa "A" fallback
function getCurrentUser() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function initialsOf(user) {
  if (!user) return "A";
  const src = user.name || user.full_name || user.email || "";
  const trimmed = String(src).trim();
  if (!trimmed) return "A";
  // Email ise @ oncesini al, isim ise ilk iki kelimenin ilk harfleri
  const isEmail = trimmed.includes("@");
  const local = isEmail ? trimmed.split("@")[0] : trimmed;
  const parts = local.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return local[0]?.toUpperCase() || "A";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export default function Topbar({ onMobileMenuClick }) {
  const navigate = useNavigate();
  const { mode, toggle: toggleTheme } = useThemeMode();

  const user = useMemo(getCurrentUser, []);
  const initials = useMemo(() => initialsOf(user), [user]);
  const displayName = user?.name || user?.email || "Admin";

  const [anchorEl, setAnchorEl] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const requestLogout = () => { closeMenu(); setConfirmOpen(true); };
  // clearAuth(): sadece token+user siler; theme tercihi vb. korunur.
  const doLogout = () => { clearAuth(); navigate("/login"); };

  return (
    <>
      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        sx={{
          bgcolor: "background.paper",
          color: "text.primary",
          borderBottom: 1,
          borderColor: "divider",
          zIndex: (t) => t.zIndex.drawer - 1,
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, md: 64 } }}>
          <IconButton
            aria-label="Menüyü aç"
            onClick={onMobileMenuClick}
            sx={{ mr: 1, display: { xs: "inline-flex", md: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700, fontSize: 16 }}>
            Miksan Plastik Admin
          </Typography>

          {/* Cmd+K hint */}
          <Box
            onClick={() => window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", ctrlKey: true }))}
            sx={{
              mr: 1.5,
              px: 1.5, py: 0.5,
              border: 1, borderColor: "divider", borderRadius: 1,
              fontSize: 12, color: "text.secondary",
              cursor: "pointer",
              display: { xs: "none", sm: "inline-flex" },
              alignItems: "center",
              gap: 0.5,
              "&:hover": { bgcolor: "action.hover" },
            }}
            title="Ctrl+K"
            aria-label="Hızlı arama (Ctrl+K)"
          >
            🔍 Hızlı Arama
            <Box component="span" sx={{ fontSize: 10, opacity: 0.7, ml: 0.5 }}>Ctrl+K</Box>
          </Box>

          <NotificationBell />

          <IconButton
            onClick={toggleTheme}
            aria-label={mode === "dark" ? "Açık temaya geç" : "Koyu temaya geç"}
            title={mode === "dark" ? "Açık tema" : "Koyu tema"}
          >
            {mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>

          <IconButton
            onClick={openMenu}
            aria-label="Kullanıcı menüsü"
            sx={{ ml: 1, p: 0 }}
            title={displayName}
          >
            <Avatar sx={{ bgcolor: "primary.main", width: 34, height: 34, fontSize: 13, fontWeight: 700 }}>
              {initials}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { mt: 0.5, minWidth: 220, borderRadius: 2 } } }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography fontWeight={700} fontSize={14} sx={{ color: "text.primary" }}>
            {displayName}
          </Typography>
          {user?.email && user?.name && (
            <Typography fontSize={12} color="text.secondary">{user.email}</Typography>
          )}
        </Box>
        <Divider />
        <MenuItem disabled>
          <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Profilim" secondary="(yakında)" secondaryTypographyProps={{ fontSize: 11 }} />
        </MenuItem>
        <Divider />
        <MenuItem onClick={requestLogout} sx={{ color: "error.main" }}>
          <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: "error.main" }} /></ListItemIcon>
          <ListItemText primary="Çıkış Yap" />
        </MenuItem>
      </Menu>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Çıkış yapılsın mı?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Oturumunuz kapatılacak ve giriş sayfasına yönlendirileceksiniz.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)}>Vazgeç</Button>
          <Button onClick={doLogout} color="error" variant="contained">Çıkış Yap</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
